import React, { useMemo, useState } from 'react'
import {
  ArrowUpIcon,
  BuildingOffice2Icon,
  EnvelopeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import {
  EMAIL_STATUS_OPTIONS,
  EmailFilterState,
  countActiveEmailFilters,
} from '../utils/emailFilterUtils'

interface FilterOption {
  id: string
  label: string
}

interface EmailFiltersModalProps {
  isOpen: boolean
  draft: EmailFilterState
  onChange: (draft: EmailFilterState) => void
  onClose: () => void
  onApply: () => void
  onClearAll: () => void
  matchCount: number
  organizationOptions: FilterOption[]
}

type CategoryId = 'status' | 'addresses' | 'organization'

const CATEGORIES: Array<{
  id: CategoryId
  label: string
  icon: React.ComponentType<{ className?: string }>
}> = [
  { id: 'status', label: 'Status', icon: FunnelIcon },
  { id: 'addresses', label: 'Addresses', icon: EnvelopeIcon },
  { id: 'organization', label: 'Organization', icon: BuildingOffice2Icon },
]

const FilterChip: React.FC<{
  label: string
  selected: boolean
  onClick: () => void
}> = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center justify-between gap-3 rounded-xl border px-4 py-2.5 text-sm transition-colors ${
      selected
        ? 'border-violet-400 bg-violet-50 text-violet-950'
        : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50'
    }`}
  >
    <span className="text-left">{label}</span>
    {selected ? (
      <CheckIcon className="h-4 w-4 shrink-0 text-violet-600" />
    ) : (
      <PlusIcon className="h-4 w-4 shrink-0 text-gray-400" />
    )}
  </button>
)

const EmailFiltersModal: React.FC<EmailFiltersModalProps> = ({
  isOpen,
  draft,
  onChange,
  onClose,
  onApply,
  onClearAll,
  matchCount,
  organizationOptions,
}) => {
  const [activeCategory, setActiveCategory] = useState<CategoryId>('status')
  const [sidebarSearch, setSidebarSearch] = useState('')

  const visibleCategories = useMemo(() => {
    const query = sidebarSearch.trim().toLowerCase()
    if (!query) return CATEGORIES
    return CATEGORIES.filter(c => c.label.toLowerCase().includes(query))
  }, [sidebarSearch])

  if (!isOpen) return null

  const updateDraft = (partial: Partial<EmailFilterState>) => onChange({ ...draft, ...partial })

  const categoryActiveCount = (id: CategoryId) => {
    switch (id) {
      case 'status':
        return draft.status ? 1 : 0
      case 'addresses':
        return (draft.fromEmail.trim() ? 1 : 0) + (draft.toEmail.trim() ? 1 : 0)
      case 'organization':
        return draft.clientOrgId ? 1 : 0
      default:
        return 0
    }
  }

  const renderStatus = () => (
    <section className="mb-8">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Status</h3>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {EMAIL_STATUS_OPTIONS.map(option => (
          <FilterChip
            key={option.id}
            label={option.label}
            selected={draft.status === option.id}
            onClick={() =>
              updateDraft({ status: draft.status === option.id ? '' : option.id })
            }
          />
        ))}
      </div>
    </section>
  )

  const renderAddresses = () => (
    <section className="mb-8 max-w-xl space-y-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Addresses</h3>
      <div>
        <label htmlFor="email-filter-from" className="mb-1 block text-sm font-medium text-gray-700">
          From (sender)
        </label>
        <input
          id="email-filter-from"
          type="text"
          className="input-field w-full text-sm"
          placeholder="Filter by sender email"
          value={draft.fromEmail}
          onChange={e => updateDraft({ fromEmail: e.target.value })}
        />
      </div>
      <div>
        <label htmlFor="email-filter-to" className="mb-1 block text-sm font-medium text-gray-700">
          To (recipient)
        </label>
        <input
          id="email-filter-to"
          type="text"
          className="input-field w-full text-sm"
          placeholder="Filter by recipient email"
          value={draft.toEmail}
          onChange={e => updateDraft({ toEmail: e.target.value })}
        />
      </div>
    </section>
  )

  const renderOrganization = () => (
    <section className="mb-8">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Client organization
      </h3>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {organizationOptions.map(option => (
          <FilterChip
            key={option.id}
            label={option.label}
            selected={draft.clientOrgId === option.id}
            onClick={() =>
              updateDraft({
                clientOrgId: draft.clientOrgId === option.id ? '' : option.id,
              })
            }
          />
        ))}
      </div>
    </section>
  )

  const renderCategoryContent = () => {
    switch (activeCategory) {
      case 'status':
        return renderStatus()
      case 'addresses':
        return renderAddresses()
      case 'organization':
        return renderOrganization()
      default:
        return null
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="flex h-[min(75vh,640px)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close filters"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-gray-500 sm:inline">
              {matchCount} email{matchCount === 1 ? '' : 's'}
            </span>
            <button type="button" className="btn-secondary text-xs" onClick={onClearAll}>
              Clear all
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700"
              onClick={onApply}
            >
              Save changes
              <ArrowUpIcon className="h-4 w-4 rotate-45" />
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1">
          <aside className="flex w-52 shrink-0 flex-col border-r border-gray-200 bg-gray-50/80">
            <div className="border-b border-gray-200 p-3">
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  className="input-field w-full pl-9 text-xs"
                  placeholder="Search filters"
                  value={sidebarSearch}
                  onChange={e => setSidebarSearch(e.target.value)}
                />
              </div>
            </div>
            <nav className="flex-1 overflow-y-auto p-2">
              {visibleCategories.map(category => {
                const Icon = category.icon
                const activeCount = categoryActiveCount(category.id)
                const isActive = activeCategory === category.id
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setActiveCategory(category.id)}
                    className={`mb-1 flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                      isActive
                        ? 'bg-violet-100 text-violet-900'
                        : 'text-gray-700 hover:bg-white hover:text-gray-900'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <Icon className="h-4 w-4 shrink-0 opacity-70" />
                      {category.label}
                    </span>
                    {activeCount > 0 && (
                      <span className="rounded-full bg-violet-600 px-1.5 py-0.5 text-xs font-medium text-white">
                        {activeCount}
                      </span>
                    )}
                  </button>
                )
              })}
            </nav>
            <div className="border-t border-gray-200 p-3 text-xs text-gray-500">
              {countActiveEmailFilters(draft)} active filter
              {countActiveEmailFilters(draft) === 1 ? '' : 's'}
            </div>
          </aside>

          <div className="min-w-0 flex-1 overflow-y-auto p-6">{renderCategoryContent()}</div>
        </div>
      </div>
    </div>
  )
}

export default EmailFiltersModal
